import React from 'react'
import {
    Box,
    Button,
    ButtonBase,
    IconButton,
    InputAdornment,
    Stack,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { ReactComponent as CloseIcon } from '../../../assets/images/Close.svg'
import { ReactComponent as DownloadIcon } from '../../../assets/images/Download_Icon.svg'
import { ReactComponent as SearchIcon } from '../../../assets/images/search-icon.svg'
import { CommentsViewFilterActionButton } from './CommentsViewFilterField'
import { getCommentsCsvBaseName } from '../CommentsView.helpers'

export const ExportButton = ({ onClick, disabled = false }: { onClick: () => void; disabled?: boolean }) => (
    <ButtonBase
        disableRipple
        disabled={disabled}
        onClick={onClick}
        sx={{
            alignItems: 'center',
            color: disabled ? 'figma.grayscale.tier4' : 'figma.primary',
            columnGap: 0.75,
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: 1.5,
            textTransform: 'uppercase',
        }}
    >
        <SvgIcon component={DownloadIcon} inheritViewBox sx={{ fontSize: '20px' }} />
        <Typography variant="16x400" color="inherit" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
            Export
        </Typography>
    </ButtonBase>
)

export const CommentsViewSearchField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
    <TextField
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search comments"
        autoComplete="off"
        InputProps={{
            startAdornment: (
                <InputAdornment position="start">
                    <SvgIcon
                        component={SearchIcon}
                        inheritViewBox
                        sx={{ fontSize: '18px', color: 'figma.grayscale.tier2' }}
                    />
                </InputAdornment>
            ),
            endAdornment: value ? (
                <InputAdornment position="end">
                    <IconButton size="small" onClick={() => onChange('')} edge="end">
                        <SvgIcon component={CloseIcon} inheritViewBox sx={{ fontSize: '18px' }} />
                    </IconButton>
                </InputAdornment>
            ) : undefined,
        }}
        sx={{
            width: { xs: '100%', md: 544 },
            '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: 'figma.grayscale.contentWrapper',
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'figma.grayscale.stroke',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'figma.grayscale.tier5',
            },
            '& .MuiInputBase-input': {
                py: 1.25,
            },
        }}
    />
)

export const CommentsViewToolbar = ({
    searchQuery,
    onSearchQueryChange,
    isResetDisabled,
    onReset,
    exportSlot,
}: {
    searchQuery: string
    onSearchQueryChange: (value: string) => void
    isResetDisabled: boolean
    onReset: () => void
    exportSlot: React.ReactNode
}) => (
    <Stack gap={3}>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <CommentsViewSearchField value={searchQuery} onChange={onSearchQueryChange} />
        </Box>

        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
            <Stack direction="row" alignItems="center" gap={1.75}>
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <CommentsViewSearchField value={searchQuery} onChange={onSearchQueryChange} />
                </Box>
                <CommentsViewFilterActionButton
                    label="Reset all filters"
                    onClick={onReset}
                    disabled={isResetDisabled}
                />
            </Stack>
            {exportSlot}
        </Stack>
    </Stack>
)

export const ExportFilenameDialog = ({
    open,
    value,
    placeholder,
    onChange,
    onClose,
    onConfirm,
}: {
    open: boolean
    value: string
    placeholder: string
    onChange: (value: string) => void
    onClose: () => void
    onConfirm: () => void
}) => (
    <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        aria-labelledby="export-comments-dialog-title"
        maxWidth="sm"
        PaperProps={{
            sx: {
                py: 4,
                px: 8,
                borderRadius: '24px',
                boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.30)',
            },
        }}
    >
        <DialogTitle id="export-comments-dialog-title" sx={{ p: 0 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                pb={1.5}
                borderBottom="1px solid"
                borderColor="figma.grayscale.stroke"
            >
                <Typography variant="22x600" color="figma.grayscale.textHier1">
                    Export Comments
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        p: 0.5,
                        color: 'figma.grayscale.textHier2',
                    }}
                >
                    <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" color="inherit" />
                </IconButton>
            </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
            <Stack gap={1.5} py={3}>
                <Typography variant="14x400" color="figma.grayscale.textHier2">
                    Keep the suggested filename or enter your own before downloading the CSV.
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    value={value}
                    placeholder={placeholder}
                    onChange={(event) => onChange(getCommentsCsvBaseName(event.target.value))}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault()
                            onConfirm()
                        }
                    }}
                    variant="outlined"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '16px',
                            bgcolor: 'figma.grayscale.contentWrapper',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'figma.grayscale.stroke',
                        },
                        '& .MuiInputBase-input': {
                            color: 'figma.grayscale.textHier1',
                            fontSize: '16px',
                            fontWeight: 500,
                            lineHeight: 1.5,
                            px: 2,
                            py: 1.75,
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Typography variant="16x500" color="figma.grayscale.textHier2">
                                    .csv
                                </Typography>
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 0 }}>
            <Box flex={1} display="flex" justifyContent="flex-end" gap={2}>
                <Button
                    onClick={onClose}
                    // @ts-expect-error mui type
                    variant="outlined-sm-button"
                    color="primary"
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    // @ts-expect-error mui type
                    variant="contained-sm-button"
                    color="primary"
                >
                    Export
                </Button>
            </Box>
        </DialogActions>
    </Dialog>
)
